/**
 * App.js - Main Application Component
 * 
 * Sets up routing and wraps everything in the ThemeProvider.
 * Routes are protected based on authentication and role.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { useAuth } from './context/AuthContext';

// Layout Components
import Header from './components/Header';

// Pages
import Login from './pages/Login';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Profile from './pages/Profile';
import Availability from './pages/Availability';
import PTORequest from './pages/PTORequest';
import UserManagement from './pages/UserManagement';
import RoomManagement from './pages/RoomManagement';
import ScheduleManagement from './pages/ScheduleManagement';
import PTOManagement from './pages/PTOManagement';
import AuditLog from './pages/AuditLog';
import ChildManagement from './pages/ChildManagement';
import ChildSchedule from './pages/ChildSchedule';
import TemplateManagement from './pages/TemplateManagement';

/**
 * ProtectedRoute - Wrapper for routes that require authentication
 */
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading, isAdmin } = useAuth();

  // Show loading while checking auth
  if (loading) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Loading...</div>;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check admin access
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/**
 * Main App Component
 */
function App() {
  const { loading } = useAuth();

  if (loading) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Loading...</div>;
  }


  return (
    <ThemeProvider>
      <div className="app">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          {/* <Route path="/signup" element={<Signup />} /> */}{/* DISABLED FOR PRODUCTION */}

          {/* Protected User Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Header />
              <UserDashboard />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Header />
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/availability" element={
            <ProtectedRoute>
              <Header />
              <Availability />
            </ProtectedRoute>
          } />
          <Route path="/pto" element={
            <ProtectedRoute>
              <Header />
              <PTORequest />
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute adminOnly>
              <Header />
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute adminOnly>
              <Header />
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/rooms" element={
            <ProtectedRoute adminOnly>
              <Header />
              <RoomManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/schedule" element={
            <ProtectedRoute adminOnly>
              <Header />
              <ScheduleManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/pto" element={
            <ProtectedRoute adminOnly>
              <Header />
              <PTOManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/audit" element={
            <ProtectedRoute adminOnly>
              <Header />
              <AuditLog />
            </ProtectedRoute>
          } />
          <Route path="/admin/students" element={
            <ProtectedRoute adminOnly>
              <Header />
              <ChildManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/student-schedule" element={
            <ProtectedRoute adminOnly>
              <Header />
              <ChildSchedule />
            </ProtectedRoute>
          } />
          <Route path="/admin/templates" element={
            <ProtectedRoute adminOnly>
              <Header />
              <TemplateManagement />
            </ProtectedRoute>
          } />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </ThemeProvider>
  );
}

export default App;
